import { Matrix4, Object3D, PerspectiveCamera, Vector3 } from "three";
import { maxHeight, wallLength } from "../controllers/wall-controllers";
import { Wall } from "../domain";

// Scratch objects
const
    obj = new Object3D(),
    v = new Vector3();

/**
 * Positions and rotates a camera to frame the gap between the first and last
 * walls.
 *
 * @param camera - the camera to modify
 * @param first - the first wall in the room
 * @param last - the last wall in the room
 * @param margin - amount of additional space above/below or left/right of the
 * gap to include in the view
 * @param target - if given, copies the center of the gap to the target vector
 * so that the orbit navigation remains consistent.
 */
export const lookAtGap = (
    camera: PerspectiveCamera,
    first: Wall,
    last: Wall,
    margin: number = 12,
    target?: Vector3,
) =>
{
    const
        lastLength = wallLength(last),
        lastEndPosition = new Vector3(lastLength, 0, 0);

    obj.position.copy(last.position);
    obj.rotation.set(last.rotation.x, last.rotation.y, last.rotation.z);
    obj.updateMatrixWorld();
    obj.localToWorld(lastEndPosition);

    const
        length = v.subVectors(first.position, lastEndPosition).length(),
        halfWidth = length / 2,
        halfHeight = Math.max(maxHeight(first), maxHeight(last)) / 2,
        // hvFov is half the camera.fov converted from degrees to radians
        hvFov = camera.fov * Math.PI / 360,
        hhFov = Math.atan(Math.tan(hvFov) * camera.aspect),
        z = Math.max(
            (halfHeight + margin) / Math.tan(hvFov),
            (halfWidth + margin) / Math.tan(hhFov)
        );
    
    obj.position.copy(first.position);
    obj.rotation.set(
        0,
        Math.atan2(
            -(lastEndPosition.z - first.position.z),
            lastEndPosition.x - first.position.x,
        ),
        0);
    obj.updateMatrixWorld();

    camera.position.x = halfWidth;
    camera.position.y = halfHeight;
    camera.position.z = z;
    camera.rotation.set(0, 0, 0);

    camera.applyMatrix4( obj.matrixWorld );

    if(target) {
        target.set(halfWidth, halfHeight, 0);
        obj.localToWorld(target);
    }
};
export default lookAtGap;
